/**
 * Lógica pura de la pantalla de Reportes: qué reportes hay, el período por defecto, la validación del
 * período antes de pedir la descarga (mismo texto que usa el backend, para que se vea igual lo dispare la
 * validación del cliente o la respuesta del servidor) y la ruta de descarga de cada uno.
 */
import type { Descarga } from "./api";
import { hoyISO } from "./format";

const MAX_DIAS_COMPROBANTES = 92;

export type ReporteId = "caja" | "gastos" | "presupuesto" | "comprobantes";

export interface Reporte {
  id: ReporteId;
  titulo: string;
  descripcion: string;
  /** Si el reporte acepta filtrar por "desde"/"hasta". */
  usaPeriodo: boolean;
  /** Si además de aceptarlo, lo exige (comprobantes: el backend no arma el ZIP sin las dos fechas). */
  periodoObligatorio: boolean;
}

export const REPORTES: readonly Reporte[] = [
  {
    id: "caja",
    titulo: "Libro de caja",
    descripcion: "Todos los movimientos del período con su saldo. Se abre con Excel.",
    usaPeriodo: true,
    periodoObligatorio: false,
  },
  {
    id: "gastos",
    titulo: "Gastos",
    descripcion: "Un renglón por gasto con proveedor, CUIT, comprobante y pagos.",
    usaPeriodo: true,
    periodoObligatorio: false,
  },
  {
    id: "presupuesto",
    titulo: "Presupuesto contra real",
    descripcion: "La tabla por rubro, a hoy.",
    usaPeriodo: false,
    periodoObligatorio: false,
  },
  {
    id: "comprobantes",
    titulo: "Comprobantes",
    descripcion: "Un ZIP con los archivos adjuntos del período. Hasta 92 días.",
    usaPeriodo: true,
    periodoObligatorio: true,
  },
];

export interface Periodo {
  desde: string;
  hasta: string;
}

/** Del día 1 del mes pasado al último día del mes pasado (el mes en curso todavía no cerró). */
export function periodoInicial(hoy: Date = new Date()): Periodo {
  const anio = hoy.getFullYear();
  const mes = hoy.getMonth();
  const desde = new Date(anio, mes - 1, 1);
  const hasta = new Date(anio, mes, 0); // día 0 del mes actual = último día del mes anterior
  return { desde: hoyISO(desde), hasta: hoyISO(hasta) };
}

/** "aaaa-mm-dd" → milisegundos UTC de esa fecha (sin correr por zona horaria). */
function aFechaUtc(iso: string): number {
  const [anio, mes, dia] = iso.split("-");
  return Date.UTC(Number(anio), Number(mes) - 1, Number(dia));
}

/** Días de diferencia entre dos fechas ISO ("aaaa-mm-dd"), sin correr por zona horaria. */
function diasEntre(desde: string, hasta: string): number {
  return Math.round((aFechaUtc(hasta) - aFechaUtc(desde)) / 86_400_000);
}

/**
 * Valida el período antes de pedir la descarga. Mismo texto que el backend para "al revés" (`app/caja/reglas.py`)
 * y para el tope de 92 días de comprobantes (`app/reportes/comprobantes.py`).
 */
export function errorPeriodo(r: Reporte, p: Periodo): string | null {
  if (!r.usaPeriodo) return null;
  if (r.periodoObligatorio && (!p.desde || !p.hasta)) return "Ingresá el período";
  if (!p.desde || !p.hasta) return null;
  if (p.desde > p.hasta) return "El período está al revés: 'desde' es posterior a 'hasta'";
  if (r.id === "comprobantes" && diasEntre(p.desde, p.hasta) > MAX_DIAS_COMPROBANTES) {
    return `El período no puede superar los ${MAX_DIAS_COMPROBANTES} días. Achicá el período.`;
  }
  return null;
}

/** "/api/fideicomisos/1/reportes/caja.csv?desde=…&hasta=…" (presupuesto nunca lleva query: no usa período). */
export function rutaReporte(fid: number, r: Reporte, p: Periodo): string {
  const extension = r.id === "comprobantes" ? "zip" : "csv";
  const base = `/api/fideicomisos/${fid}/reportes/${r.id}.${extension}`;
  if (!r.usaPeriodo) return base;
  const partes: string[] = [];
  if (p.desde) partes.push(`desde=${p.desde}`);
  if (p.hasta) partes.push(`hasta=${p.hasta}`);
  return partes.length > 0 ? `${base}?${partes.join("&")}` : base;
}

/** Dispara la descarga en el navegador: un `<a download>` efímero con una URL de objeto. */
export function guardarArchivo(d: Descarga): void {
  const url = URL.createObjectURL(d.blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = d.nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
